import React, { useState } from 'react';
import { Search, Filter, Plus, Edit2, Trash2, X, Check } from 'lucide-react';
import { getApiUrl } from '../../../utils/apiConfig';
import './styles.css';

const ROLE_COLORS = {
  user: { bg: '#f3f4f6', color: '#4b5563' },
  zonal: { bg: '#dbeafe', color: '#1d4ed8' },
  admin: { bg: '#fef9c3', color: '#a16207' },
  global: { bg: '#e0e7ff', color: '#4f46e5' },
};

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '',
  role: 'user', region: '', status: 'inactive',
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteId, setDeleteId] = useState(null);

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/users`);
      const data = await res.json();
      setUsers(data || []);
    } catch (err) { console.log("Failed to load users", err); }
  };

  const filtered = users.filter(u =>
    [u.firstName, u.lastName, u.email, u.role, u.region].some(v =>
      String(v || '').toLowerCase().includes(search.toLowerCase())
    )
  );

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ firstName: u.firstName, lastName: u.lastName, email: u.email, phone: u.phone, role: u.role, region: u.region, status: u.status });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.email || !editUser) return;

    let finalRole = form.role;
    if (form.role !== 'admin') {
      finalRole = form.status === 'active' ? 'zonal' : 'user';
    }
    const payload = { ...form, role: finalRole };

    await fetch(`${getApiUrl()}/api/users/${editUser.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    setShowModal(false);
    fetchUsers();
  };

  const handleDelete = async (id) => {
    await fetch(`${getApiUrl()}/api/users/${id}`, { method: 'DELETE' });
    setDeleteId(null);
    fetchUsers();
  };

  const inlineChangeRole = async (id, newRole) => {
    const user = users.find(u => u.id === id);
    if (!user || user.role === newRole) return;

    // Optimistic UI update
    setUsers(prev => prev.map(u => u.id === id ? { ...u, role: newRole } : u));

    try {
      await fetch(`${getApiUrl()}/api/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, role: newRole })
      });
      fetchUsers();
    } catch (err) {
      console.error(err);
      fetchUsers(); // Revert on failure
    }
  };

  const toggleStatus = async (id) => {
    const user = users.find(u => u.id === id);
    if (!user) return;
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    let newRole = user.role;
    if (user.role !== 'admin') {
      newRole = newStatus === 'active' ? 'zonal' : 'user';
    }

    // Optimistic UI update
    setUsers(prev => prev.map(u => u.id === id ? { ...u, status: newStatus, role: newRole } : u));

    try {
      await fetch(`${getApiUrl()}/api/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, status: newStatus, role: newRole })
      });
      fetchUsers();
    } catch (err) {
      console.error(err);
      fetchUsers(); // Revert on failure
    }
  };

  const handleApprove = async (id) => {
    const res = await fetch(`${getApiUrl()}/api/users/${id}/approve`, { method: 'POST' });
    if (res.ok) {
      showToast('User approved successfully!');
      fetchUsers();
    } else {
      const err = await res.text();
      showToast('Error approving user: ' + err);
    }
  };


  const [umToast, setUmToast] = useState('');
  const showToast = (msg) => { setUmToast(msg); setTimeout(() => setUmToast(''), 3500); };

  return (
    <div className="um-page">
      {umToast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, background: '#1e293b', color: '#fff', padding: '12px 20px',
          borderRadius: 10, zIndex: 9999, fontSize: 14, fontWeight: 500
        }}>
          {umToast}
        </div>
      )}
      <div className="um-page-header">
        <div>
          <h2>User Management</h2>
          <p>Manage platform users, roles and access</p>
        </div>
      </div>

      {/* Stats */}
      <div className="um-stats">
        {[
          { label: 'Total Users', value: users.length, bg: '#ede9fe', color: '#5b21b6' },
          { label: 'Active', value: users.filter(u => u.status === 'active').length, bg: '#dcfce7', color: '#16a34a' },
          { label: 'Inactive', value: users.filter(u => u.status === 'inactive').length, bg: '#fee2e2', color: '#dc2626' },
          { label: 'Total Reports', value: users.reduce((acc, u) => acc + (u.reportCount || 0), 0), bg: '#e0f2fe', color: '#0369a1' },
        ].map(s => (
          <div className="um-stat-card" key={s.label}>
            <div className="um-stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="um-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="um-panel">
        <div className="um-toolbar">
          <div className="um-search">
            <Search size={14} color="#9ca3af" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="um-filter-btn"><Filter size={14} /> Filter</button>
        </div>

        <p className="um-count">Showing {filtered.length} users</p>

        <div className="um-table-wrapper" style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
          <table className="um-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Region</th>
                <th>Reports</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const rc = ROLE_COLORS[u.role] ?? { bg: '#f3f4f6', color: '#374151' };
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="um-user-cell">
                        <div className="um-av" style={{ background: rc.bg, color: rc.color }}>
                          {(u.firstName?.[0] || u.email?.[0] || '?').toUpperCase()}
                        </div>
                        <span className="um-name">{u.firstName ? `${u.firstName} ${u.lastName || ''}` : 'Pending Invite'}</span>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>{u.phone || '—'}</td>
                    <td>
                      <select
                        value={u.role.toLowerCase()}
                        onChange={(e) => inlineChangeRole(u.id, e.target.value)}
                        className="um-role-select"
                        style={{
                          background: rc.bg,
                          color: rc.color,
                          border: 'none',
                          borderRadius: '12px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          outline: 'none',
                          WebkitAppearance: 'none',
                          textTransform: 'capitalize'
                        }}
                      >
                        <option value="user">User</option>
                        <option value="zonal">Zonal</option>
                        <option value="global">Global</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>{u.region}</td>
                    <td>
                      <span style={{
                        background: '#e0f2fe',
                        color: '#0369a1',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'inline-block'
                      }}>
                        {u.reportCount || 0}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`um-status-badge ${u.status}`}
                        onClick={() => toggleStatus(u.id)}
                        title="Click to toggle"
                      >
                        {u.status}
                      </button>
                    </td>
                    <td>{u.joined}</td>
                    <td>
                      <div className="um-actions">
                        {/* Approve */}
                        {u.status === 'inactive' && (
                          <button
                            className="um-icon-btn"
                            style={{ color: '#16a34a', borderColor: '#16a34a', background: '#dcfce7' }}
                            onClick={() => handleApprove(u.id)}
                            title="Approve User"
                          >
                            <Check size={14} />
                          </button>
                        )}
                        {u.status === 'inactive' && u.inviteToken && (
                          <button
                            className="um-icon-btn copy"
                            onClick={() => {
                              const link = `${window.location.origin}/invite?token=${u.inviteToken}`;
                              navigator.clipboard.writeText(link);
                              alert('Invite link copied to clipboard!');
                            }}
                            title="Copy Invite Link"
                          >
                            <Plus size={14} style={{ transform: 'rotate(45deg)' }} />
                          </button>
                        )}
                        <button className="um-icon-btn edit" onClick={() => openEdit(u)} title="Edit">
                          <Edit2 size={14} />
                        </button>
                        <button className="um-icon-btn del" onClick={() => setDeleteId(u.id)} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="um-overlay">
          <div className="um-modal">
            <div className="um-modal-header">
              <h3>Edit User</h3>
              <button className="um-modal-close" onClick={() => setShowModal(false)}>
                <X size={16} />
              </button>
            </div>

            <div className="um-modal-body">
              <div className="um-form-grid">
                <div className="um-field">
                  <label>First Name</label>
                  <input type="text" value={form.firstName || ''}
                    onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
                    placeholder="First name" />
                </div>
                <div className="um-field">
                  <label>Last Name</label>
                  <input type="text" value={form.lastName || ''}
                    onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
                    placeholder="Last name" />
                </div>
                <div className="um-field um-field-full">
                  <label>Email Address</label>
                  <input type="email" value={form.email || ''} disabled={true}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="email@loveworld.com" />
                </div>
                <div className="um-field">
                  <label>Role</label>
                  <select value={form.role || (form.status === 'active' ? 'zonal' : 'user')}
                    onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                    {form.status !== 'active' && <option value="user">User (Inactive)</option>}
                    <option value="zonal">Zonal Manager</option>
                    <option value="admin">System Administrator</option>
                  </select>
                </div>
                <div className="um-field">
                  <label>Region</label>
                  <select value={form.region || 'Global'}
                    onChange={e => setForm(p => ({ ...p, region: e.target.value }))}>
                    {['Global', 'North America', 'South America', 'Europe', 'Africa', 'Asia Pacific'].map(r => (
                      <option key={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="um-field">
                  <label>Phone Number</label>
                  <input type="text" value={form.phone || ''}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+234..." />
                </div>
                <div className="um-field">
                  <label>Status</label>
                  <select value={form.status}
                    onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="um-modal-footer">
              <button className="um-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="um-btn-primary" onClick={handleSave}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="um-overlay">
          <div className="um-modal um-modal-sm">
            <div className="um-modal-header">
              <h3>Delete User</h3>
              <button className="um-modal-close" onClick={() => setDeleteId(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="um-modal-body">
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
            </div>
            <div className="um-modal-footer">
              <button className="um-btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="um-btn-danger" onClick={() => handleDelete(deleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}